import { updateSiemprePlusData } from 'actions/siemprePlus';
import { receiveProfile } from 'actions/costapass';
import store from '@/store';
import errorMonitoring from '../../errorMonitoring';

/**
 * Gets the sessionToken from reserhubAccount user
 * @returns {string|null} The sessionToken or null if not available
 */
function getReserhubSessionToken() {
  try {
    const reserhubAccount = store.getState().reserhubAccount?.toJS() ?? {};
    const { user } = reserhubAccount;
    return user?.sessionToken || null;
  } catch (error) {
    return null;
  }
}

/**
 * Adapts unified account connected account to siemprePlus reducer format
 * @param {Object} connectedAccount - Connected account from unified API
 * @returns {Object|null} Adapted data for siemprePlus reducer or null if invalid
 */
export function adaptConnectedAccountToSiemprePlus(connectedAccount) {
  if (!connectedAccount?.profile || connectedAccount.provider !== 'doters') {
    return null;
  }

  try {
    const { profile, wallet } = connectedAccount;
    // Use sessionToken from reserhubAccount for loyalty systems
    const sessionToken = getReserhubSessionToken();

    return {
      user: {
        token: sessionToken,
        email: profile.email,
        firstName: profile.firstName,
        lastName: profile.lastName,
        memberId: profile.memberId,
        loyaltyName: 'doters',
        availablePoints: wallet?.balance?.available || 0,
        level: profile.level || null,
        pointsFactor: wallet?.monetaryEquivalent?.conversionRate ?? null,
      },
      loyaltyName: 'doters',
    };
  } catch (error) {
    errorMonitoring.notify({
      error,
      info: {
        message: 'Failed to adapt connected account to siemprePlus',
        connectedAccount,
      },
    });
    return null;
  }
}

/**
 * Adapts unified account connected account to costapass reducer format
 * @param {Object} connectedAccount - Connected account from unified API
 * @returns {Object|null} Adapted data for costapass reducer or null if invalid
 */
export function adaptConnectedAccountToCostapass(connectedAccount) {
  if (!connectedAccount?.profile || connectedAccount.provider !== 'travelpass') {
    return null;
  }

  try {
    const { profile, wallet } = connectedAccount;
    // Use sessionToken from reserhubAccount for loyalty systems
    const sessionToken = getReserhubSessionToken();

    return {
      profile: {
        token: sessionToken,
        email: profile.email,
        name: profile.firstName,
        firstName: profile.firstName,
        lastName: profile.lastName,
        memberId: profile.memberId,
        balance: wallet?.balance?.available || 0,
        level: profile.level || null,
        pointsFactor: wallet?.monetaryEquivalent?.conversionRate ?? null,
      },
      loyaltyName: 'travelpass',
    };
  } catch (error) {
    errorMonitoring.notify({
      error,
      info: {
        message: 'Failed to adapt connected account to costapass',
        connectedAccount,
      },
    });
    return null;
  }
}

/**
 * Updates legacy Redux reducers with adapted data from connected accounts
 * @param {Function} dispatch - Redux dispatch function
 * @param {Array} connectedAccounts - Array of connected accounts from unified API
 */
export function updateLegacyReducersWithConnectedAccounts(dispatch, connectedAccounts) {
  if (!Array.isArray(connectedAccounts)) {
    return;
  }

  try {
    connectedAccounts.forEach((connectedAccount) => {
      if (connectedAccount.provider === 'doters') {
        const adaptedData = adaptConnectedAccountToSiemprePlus(connectedAccount);
        if (adaptedData) {
          dispatch(updateSiemprePlusData(adaptedData.user, adaptedData.loyaltyName));
        }
      } else if (connectedAccount.provider === 'travelpass') {
        const adaptedData = adaptConnectedAccountToCostapass(connectedAccount);
        if (adaptedData) {
          dispatch(receiveProfile(adaptedData.profile, adaptedData.loyaltyName));
        }
      }
    });
  } catch (error) {
    errorMonitoring.notify({
      error,
      info: {
        message: 'Failed to update legacy reducers with connected accounts',
        connectedAccounts,
      },
    });
  }
}
